import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";
import MailboxEditModal from "../components/MailboxEditModal";
import MailboxEmailModal from "../components/MailboxEmailModal";
import MailboxFilterModal, { MAILBOX_OCCUPANCY_FILTERS } from "../components/MailboxFilterModal";
import MailboxProfileModal from "../components/MailboxProfileModal";
import MailboxTable from "../components/MailboxTable";
import { tenantDisplayName } from "../utils/tenantProfile";

export default function Mailboxes() {
  const location = useLocation();
  const [mailboxes, setMailboxes] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [profileMailbox, setProfileMailbox] = useState(null);
  const [emailMailbox, setEmailMailbox] = useState(null);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [occupancyFilter, setOccupancyFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [showOccupancyColors, setShowOccupancyColors] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editError, setEditError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEmailDraftLoading, setIsEmailDraftLoading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const fetchMailboxes = useCallback(async () => {
    const data = await apiFetch("/mailboxes");
    setMailboxes(data);
  }, []);

  const fetchTenants = useCallback(async () => {
    const data = await apiFetch("/tenants");
    setTenants(data);
  }, []);

  const loadPageData = useCallback(async () => {
    setError("");
    setIsLoading(true);
    try {
      await Promise.all([fetchMailboxes(), fetchTenants()]);
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Mailboxes", action: "loadPageData" });
    } finally {
      setIsLoading(false);
    }
  }, [fetchMailboxes, fetchTenants]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const openEditor = (mailbox) => {
    setSelectedMailbox(mailbox);
    setEditError("");
    setSuccess("");
  };

  const updateMailbox = async (data) => {
    if (!selectedMailbox) return;
    setEditError("");
    setIsSaving(true);
    try {
      await apiFetch(`/mailboxes/${selectedMailbox.id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      setSelectedMailbox(null);
      setSuccess(`Mailbox #${selectedMailbox.number} updated successfully.`);
      await fetchMailboxes();
    } catch (err) {
      setEditError(err.message);
      logClientError(err, { page: "Mailboxes", action: "updateMailbox", mailboxId: selectedMailbox.id });
    } finally {
      setIsSaving(false);
    }
  };

  const createTenant = async (profile) => {
    try {
      const tenant = await apiFetch("/tenants", {
        method: "POST",
        body: JSON.stringify(profile),
      });
      setTenants((current) => [...current, tenant]
        .sort((left, right) => tenantDisplayName(left).localeCompare(tenantDisplayName(right))));
      return tenant;
    } catch (err) {
      logClientError(err, { page: "Mailboxes", action: "createTenantForMailbox", mailboxId: selectedMailbox?.id });
      throw err;
    }
  };

  const openEmailComposer = async (mailbox) => {
    setEmailMailbox(mailbox);
    setEmailDraft(null);
    setEmailMessage("");
    setEmailError("");
    setIsEmailDraftLoading(true);
    setSuccess("");

    try {
      const draft = await apiFetch(`/mailboxes/${mailbox.id}/email-draft`);
      setEmailDraft(draft);
      setEmailMessage(draft.message);
    } catch (err) {
      setEmailError(err.message);
      logClientError(err, { page: "Mailboxes", action: "loadMailboxEmailDraft", mailboxId: mailbox.id });
    } finally {
      setIsEmailDraftLoading(false);
    }
  };

  const closeEmailComposer = () => {
    if (isSendingEmail) return;
    setEmailMailbox(null);
    setEmailDraft(null);
    setEmailMessage("");
    setEmailError("");
  };

  const sendMailboxEmail = async (event) => {
    event.preventDefault();
    if (!emailMailbox || !emailDraft) return;

    setError("");
    setSuccess("");
    setEmailError("");
    setIsSendingEmail(true);
    try {
      await apiFetch(`/mailboxes/${emailMailbox.id}/send-email`, {
        method: "POST",
        body: JSON.stringify({ message: emailMessage }),
      });
      setEmailMailbox(null);
      setEmailDraft(null);
      setEmailMessage("");
      setEmailError("");
      setSuccess("Mail notification sent successfully.");
      await fetchMailboxes();
    } catch (err) {
      setEmailError(err.message);
      logClientError(err, { page: "Mailboxes", action: "sendMailboxEmail", mailboxId: emailMailbox.id });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const filterLabel = MAILBOX_OCCUPANCY_FILTERS.find(({ value }) => value === occupancyFilter)?.label || "All";
  const visibleMailboxes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return mailboxes.filter((mailbox) => {
      const tenantValues = [
        mailbox.tenant ? tenantDisplayName(mailbox.tenant) : null,
        mailbox.tenant?.email,
        mailbox.tenant?.businessName,
      ];
      const matchesSearch = !query
        || mailbox.number.toLowerCase().includes(query)
        || tenantValues.some((value) => value?.toLowerCase().includes(query));
      const isOccupied = Boolean(mailbox.tenant);
      const matchesOccupancy = occupancyFilter === "all"
        || (occupancyFilter === "occupied" && isOccupied)
        || (occupancyFilter === "available" && !isOccupied);
      const matchesDashboardShortcut = location.state?.filter !== "withMail" || mailbox.hasMail;
      return matchesSearch && matchesOccupancy && matchesDashboardShortcut;
    });
  }, [location.state, mailboxes, occupancyFilter, searchQuery]);

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Mailbox Manager</p>
          <h2>Mailboxes</h2>
        </div>
      </div>

      <div className="admin-mailbox-toolbar">
        <label className="admin-tenant-search">
          <span className="admin-visually-hidden">Search mailboxes by number or assigned tenant</span>
          <input
            type="search"
            placeholder="Search by mailbox number or assigned tenant"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
        <div className="admin-mailbox-toolbar-actions">
          <div className="admin-tenant-filter-control">
            <button className="admin-button admin-button-secondary" type="button" aria-haspopup="dialog" onClick={() => setFilterOpen(true)}>
              Filter
            </button>
            <span className="admin-filter-summary">Filter: {filterLabel}</span>
          </div>
          <label className="admin-occupancy-color-toggle">
            <input
              type="checkbox"
              checked={showOccupancyColors}
              onChange={(event) => setShowOccupancyColors(event.target.checked)}
            />
            <span className="admin-toggle-track" aria-hidden="true" />
            <span>Occupancy colors</span>
          </label>
          <div className="admin-view-toggle" role="group" aria-label="Mailbox display">
            <button type="button" aria-pressed={viewMode === "list"} onClick={() => setViewMode("list")}>List</button>
            <button type="button" aria-pressed={viewMode === "grid"} onClick={() => setViewMode("grid")}>Grid</button>
          </div>
        </div>
      </div>

      {error && <p className="admin-empty-state admin-error-state">{error}</p>}
      {success && <p className="admin-empty-state admin-success-state">{success}</p>}

      {isLoading ? (
        <p className="admin-empty-state">Loading mailboxes...</p>
      ) : (
        <>
          <div className="admin-results-summary">
            <span>Showing {visibleMailboxes.length} of {mailboxes.length} mailboxes</span>
            {showOccupancyColors && (
              <span className="admin-occupancy-legend" aria-label="Occupancy color key">
                <span><i className="admin-occupancy-swatch is-occupied" />Occupied</span>
                <span><i className="admin-occupancy-swatch is-available" />Not occupied</span>
              </span>
            )}
          </div>
          <MailboxTable
            mailboxes={visibleMailboxes}
            viewMode={viewMode}
            showOccupancyColors={showOccupancyColors}
            onEdit={openEditor}
            onSendEmail={openEmailComposer}
            onViewProfile={setProfileMailbox}
          />
        </>
      )}

      {filterOpen && (
        <MailboxFilterModal
          value={occupancyFilter}
          onApply={(selection) => {
            setOccupancyFilter(selection);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {profileMailbox && <MailboxProfileModal mailbox={profileMailbox} onClose={() => setProfileMailbox(null)} />}

      {emailMailbox && (
        <MailboxEmailModal
          mailbox={emailMailbox}
          draft={emailDraft}
          message={emailMessage}
          isLoading={isEmailDraftLoading}
          isSending={isSendingEmail}
          error={emailError}
          onMessageChange={setEmailMessage}
          onSend={sendMailboxEmail}
          onClose={closeEmailComposer}
        />
      )}

      {selectedMailbox && (
        <MailboxEditModal
          mailbox={selectedMailbox}
          tenants={tenants}
          isSaving={isSaving}
          submitError={editError}
          onSubmit={updateMailbox}
          onCreateTenant={createTenant}
          onClose={() => {
            if (!isSaving) {
              setSelectedMailbox(null);
              setEditError("");
            }
          }}
        />
      )}
    </div>
  );
}
